import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Shield from 'lucide-react/dist/esm/icons/shield';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import XCircle from 'lucide-react/dist/esm/icons/x-circle';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Mail from 'lucide-react/dist/esm/icons/mail';
import Activity from 'lucide-react/dist/esm/icons/activity';
import Eye from 'lucide-react/dist/esm/icons/eye';
import EyeOff from 'lucide-react/dist/esm/icons/eye-off';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
import Settings from 'lucide-react/dist/esm/icons/settings';
import Lock from 'lucide-react/dist/esm/icons/lock';
import X from 'lucide-react/dist/esm/icons/x';

const AdminContactDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Password change modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('https://omc-contact-form.adaptivepty.workers.dev/verify-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const result = await response.json();

      if (response.ok) {
        setIsAuthenticated(true);
        fetchStats();
      } else {
        setError(result.error || 'Incorrect password');
      }
    } catch (err) {
      setError('Failed to verify password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://omc-contact-form.adaptivepty.workers.dev/admin-stats', {
        headers: {
          'X-Admin-Password': password,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordChangeError('');
    setPasswordChangeSuccess('');

    // Validation
    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      setPasswordChangeError('New passwords do not match');
      return;
    }

    if (passwordChangeData.newPassword.length < 8) {
      setPasswordChangeError('New password must be at least 8 characters long');
      return;
    }

    try {
      const response = await fetch('https://omc-contact-form.adaptivepty.workers.dev/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': password,
        },
        body: JSON.stringify({
          currentPassword: passwordChangeData.currentPassword,
          newPassword: passwordChangeData.newPassword,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setPasswordChangeSuccess('Password changed successfully! Please login again with your new password.');
        setTimeout(() => {
          setShowPasswordModal(false);
          setIsAuthenticated(false);
          setPassword('');
          setPasswordChangeData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
        }, 2000);
      } else {
        setPasswordChangeError(result.error || 'Failed to change password');
      }
    } catch (err) {
      setPasswordChangeError('Failed to change password. Please try again.');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      const interval = setInterval(fetchStats, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md"
        >
          <div className="flex items-center justify-center mb-6">
            <Shield className="h-12 w-12 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Admin Dashboard
          </h1>
          <p className="text-center text-gray-600 mb-6">
            Contact Form Security Monitor
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                  placeholder="Enter admin password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Access Dashboard'
              )}
            </button>

            <div className="text-center">
  <a
    href="/#/admin-contact/forgot-password"
    className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
    title="Forgot password"
  >
    Forgot password?
  </a>
</div>
          </form>

          <p className="mt-6 text-xs text-center text-gray-500">
            OMC Chartered Accountants - Contact Form Security
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Contact Form Security</h1>
                <p className="text-sm text-gray-500">OMC Chartered Accountants</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {lastUpdated && (
                <div className="text-sm text-gray-500">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </div>
              )}
              <button
                onClick={fetchStats}
                disabled={loading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors"
              >
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </button>
              <button
                onClick={() => setIsAuthenticated(false)}
                className="text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md relative"
          >
            <button
              onClick={() => {
                setShowPasswordModal(false);
                setPasswordChangeError('');
                setPasswordChangeSuccess('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>

            <div className="flex items-center justify-center mb-6">
              <Lock className="h-12 w-12 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">
              Change Password
            </h2>

            {passwordChangeSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-sm text-green-600 flex items-start">
                <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>{passwordChangeSuccess}</span>
              </div>
            )}

            {passwordChangeError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-sm text-red-600">
                {passwordChangeError}
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordChangeData.currentPassword}
                    onChange={(e) =>
                      setPasswordChangeData({ ...passwordChangeData, currentPassword: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                    placeholder="Enter current password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordChangeData.newPassword}
                    onChange={(e) =>
                      setPasswordChangeData({ ...passwordChangeData, newPassword: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                    placeholder="Enter new password (min 8 characters)"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordChangeData.confirmPassword}
                  onChange={(e) =>
                    setPasswordChangeData({ ...passwordChangeData, confirmPassword: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Change Password
              </button>
            </form>
          </motion.div>
        </div>
      )}

      {/* Main Content - Stats Dashboard */}
      <main className="container mx-auto px-4 py-8">
        {loading && !stats ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
        ) : stats ? (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Submissions Today"
                value={stats.totalSubmissionsToday || 0}
                icon={Mail}
                color="blue"
                subtitle="Successful emails sent"
              />
              <StatCard
                title="Blocked Attempts"
                value={stats.blockedToday || 0}
                icon={XCircle}
                color="red"
                subtitle="Security violations"
              />
              <StatCard
                title="Rate Limit Hits"
                value={stats.rateLimitHits || 0}
                icon={AlertTriangle}
                color="yellow"
                subtitle="IP/Email throttled"
              />
              <StatCard
                title="Success Rate"
                value={`${stats.successRate || 100}%`}
                icon={CheckCircle}
                color="green"
                subtitle="Valid submissions"
              />
            </div>

            {/* Recent Blocked Attempts */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-2" />
                Recent Blocked Attempts
              </h2>
              
              {stats.recentBlocked && stats.recentBlocked.length > 0 ? (
                <div className="space-y-3">
                  {stats.recentBlocked.map((attempt, index) => (
                    <BlockedAttemptCard key={index} attempt={attempt} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No blocked attempts today</p>
              )}
            </div>

            {/* Rate Limit Status */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Activity className="h-6 w-6 text-blue-500 mr-2" />
                Active Rate Limits
              </h2>
              
              {stats.activeRateLimits && stats.activeRateLimits.length > 0 ? (
                <div className="space-y-3">
                  {stats.activeRateLimits.map((limit, index) => (
                    <RateLimitCard key={index} limit={limit} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No active rate limits</p>
              )}
            </div>

            {/* Security Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Security Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Turnstile Verifications</div>
                  <div className="text-2xl font-bold text-gray-900">{stats.turnstileChecks || 0}</div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Honeypot Catches</div>
                  <div className="text-2xl font-bold text-gray-900">{stats.honeypotCatches || 0}</div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Disposable Emails Blocked</div>
                  <div className="text-2xl font-bold text-gray-900">{stats.disposableEmailsBlocked || 0}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-12">
            <Activity className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No data available. Click refresh to load statistics.</p>
          </div>
        )}
      </main>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    green: 'bg-green-50 text-green-600 border-green-200',
  };

  const borderColor = color === 'yellow' ? '#CA8A03' : color;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-6 border-l-4"
      style={{ borderColor }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
      <div className="text-3xl font-bold text-gray-900 mb-1">{value}</div>
      <div className="text-sm font-medium text-gray-600">{title}</div>
      <div className="text-xs text-gray-500 mt-1">{subtitle}</div>
    </motion.div>
  );
};

// Blocked Attempt Card
const BlockedAttemptCard = ({ attempt }) => {
  return (
    <div className="border border-red-200 rounded-lg p-4 bg-red-50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <span className="font-semibold text-red-900">{attempt.reason}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-600">IP:</span>
              <span className="ml-2 font-mono text-gray-900">{attempt.ip}</span>
            </div>
            <div>
              <span className="text-gray-600">Email:</span>
              <span className="ml-2 text-gray-900">{attempt.email || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">Time:</span>
              <span className="ml-2 text-gray-900">{new Date(attempt.timestamp * 1000).toLocaleString()}</span>
            </div>
            <div>
              <span className="text-gray-600">Details:</span>
              <span className="ml-2 text-gray-900">{attempt.details}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Rate Limit Card
const RateLimitCard = ({ limit }) => {
  const percentage = (limit.count / limit.max) * 100;
  
  return (
    <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Clock className="h-5 w-5 text-yellow-600" />
          <span className="font-semibold text-yellow-900">{limit.type}</span>
        </div>
        <span className="text-sm font-mono text-yellow-900">
          {limit.count} / {limit.max}
        </span>
      </div>
      <div className="w-full bg-yellow-200 rounded-full h-2 mb-2">
        <div
          className="bg-yellow-600 h-2 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
      <div className="text-sm text-yellow-800">
        <span className="font-medium">{limit.identifier}</span> - Resets in {limit.timeLeft}
      </div>
    </div>
  );
};

export default AdminContactDashboard;